<script setup>
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import TabHeading from "@/components/kit-settings/partials/TabHeading.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import LoadingModal from "@/components/approval-center/modals/LoadingModal.vue";
import AdvancedRevisionModal from "@/components/approval-center/modals/advanced-revision/AdvancedRevisionModal.vue";
import useAdvancedRevisionFeedback from "@/composables/approval-center/advanced-revision/useAdvancedRevisionFeedback";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";

import {
  showModal,
  showLoadingModal,
  reset,
  getToneOfVoice,
  reviseToneOfVoice,
} from "@/composables/kit-settings/useToneOfVoice";
import ZeroContent from "@/components/global-themed/ZeroContent.vue";

const {revisionErrors} = useApprovalFlow();
const {data: toneOfVoiceData, isPending: loadingToneOfVoice} = getToneOfVoice();

const {
  fullFeedback,
  feedbackItems,
  mappedFeedbackItems,
} = useAdvancedRevisionFeedback();

const {mutate: handleConfirm, isPending: revisingToneOfVoice, isError} = reviseToneOfVoice();

const handleClose = () => {
  feedbackItems.value.clear();
  fullFeedback.value = '';
  reset();
};
</script>

<template>
  <div class="tw-h-full tw-flex tw-flex-col tw-gap-y-6 tw-p-8 tw-w-full tw-border tw-border-neutral-300 tw-rounded-xl tw-bg-white tw-relative">
    <TabHeading
      heading="Your Tone of Voice"
      icon="icons/megaphone"
    />

    <div class="tw-prose tw-max-w-none tw-flex tw-items-start tw-gap-8 tw-w-full tw-border tw-rounded-lg tw-overflow-scroll tw-p-8 tw-h-full tw-border-neutral-300">
      <LoadingBlock v-if="loadingToneOfVoice" />

      <div
        v-else-if="toneOfVoiceData?.tone_of_voice"
        v-html="toneOfVoiceData.tone_of_voice"
      />

      <ZeroContent v-else>
        It looks like your Tone of Voice Guide hasn’t been created yet. <br>
        If it’s been more than four days since you completed onboarding, please contact support so we can sort it for you
      </ZeroContent>
    </div>

    <div class="tw-flex tw-self-end">
      <DefaultButton
        variant="primary-inverted"
        class="tw-gap-2"
        :disabled="!toneOfVoiceData?.tone_of_voice"
        @click="showModal = true"
      >
        <svg-vue
          icon="icons/chat-check"
          class="tw-h-6 tw-w-6"
        />
        Give feedback
      </DefaultButton>
    </div>
  </div>

  <AdvancedRevisionModal
    v-if="showModal"
    title="Edit Tone of Voice"
    type="Tone of Voice"
    :approvable-content="toneOfVoiceData.tone_of_voice_markdown || ''"
    @confirm="handleConfirm({
      feedback: fullFeedback,
      individualFeedbacks: mappedFeedbackItems,
    })"
    @close="handleClose"
  />

  <LoadingModal
    v-if="showLoadingModal && !revisionErrors && !isError"
    :loading="revisingToneOfVoice"
    @continue="handleClose"
  />
</template>
